'use client'

import { useMemo } from 'react'
import Link from 'next/link'
import { Sun, Sparkles } from 'lucide-react'

/**
 * Daily Horoscope — fully static, no backend.
 *
 * Content "changes daily" via a date-seeded pseudo-random pick: today's
 * date produces a deterministic seed, and each sign deterministically
 * selects one variant from a small pool. Same day = same reading for
 * everyone; next day = a different rotation. No API, no fetch — works
 * perfectly with `output: 'export'`.
 */

type Sign = {
  name: string
  glyph: string
  dates: string
  element: 'Fire' | 'Earth' | 'Air' | 'Water'
  ruler: string
}

const SIGNS: Sign[] = [
  { name: 'Aries', glyph: '♈', dates: 'Mar 21 – Apr 19', element: 'Fire', ruler: 'Mars' },
  { name: 'Taurus', glyph: '♉', dates: 'Apr 20 – May 20', element: 'Earth', ruler: 'Venus' },
  { name: 'Gemini', glyph: '♊', dates: 'May 21 – Jun 20', element: 'Air', ruler: 'Mercury' },
  { name: 'Cancer', glyph: '♋', dates: 'Jun 21 – Jul 22', element: 'Water', ruler: 'Moon' },
  { name: 'Leo', glyph: '♌', dates: 'Jul 23 – Aug 22', element: 'Fire', ruler: 'Sun' },
  { name: 'Virgo', glyph: '♍', dates: 'Aug 23 – Sep 22', element: 'Earth', ruler: 'Mercury' },
  { name: 'Libra', glyph: '♎', dates: 'Sep 23 – Oct 22', element: 'Air', ruler: 'Venus' },
  { name: 'Scorpio', glyph: '♏', dates: 'Oct 23 – Nov 21', element: 'Water', ruler: 'Mars / Pluto' },
  { name: 'Sagittarius', glyph: '♐', dates: 'Nov 22 – Dec 21', element: 'Fire', ruler: 'Jupiter' },
  { name: 'Capricorn', glyph: '♑', dates: 'Dec 22 – Jan 19', element: 'Earth', ruler: 'Saturn' },
  { name: 'Aquarius', glyph: '♒', dates: 'Jan 20 – Feb 18', element: 'Air', ruler: 'Saturn / Uranus' },
  { name: 'Pisces', glyph: '♓', dates: 'Feb 19 – Mar 20', element: 'Water', ruler: 'Jupiter / Neptune' },
]

/* Shared content pools — a sign+date combination indexes into each pool.
   Pools are intentionally large so that, across all 12 signs on any given
   day (and day to day for a single sign), readings rarely repeat. */
const OVERVIEW = [
  'The cosmos rewards bold but measured action today. A door you assumed was closed quietly reopens — walk through it with confidence.',
  'A reflective energy surrounds you. Slow mornings and honest conversations set the tone for a deeply productive afternoon.',
  'Momentum builds in your favour. What felt stuck for weeks finally moves — trust the timing and keep your focus narrow.',
  'Today asks for patience over force. The universe is arranging something behind the scenes; let it unfold without rushing.',
  'Creative and social energy peaks. An idea shared casually could grow into something far bigger than you expect.',
  'Grounding is your theme. Tend to the small, practical things and a larger sense of clarity will follow naturally.',
  'A turning point in a relationship or project arrives. Speak from the heart and the rest aligns on its own.',
  'The planets favour fresh starts. Begin the thing you keep postponing, even if only the first small step.',
  'Clarity arrives after a foggy stretch. A choice that confused you suddenly makes simple sense.',
  'Your intuition is unusually sharp today. Trust the quiet nudge over the loud opinion.',
  'A day for tying up loose ends. Closing one small chapter frees energy you did not know you were spending.',
  'Generosity circles back to you. A kindness you offer quietly returns in an unexpected form.',
  'Observe before you act. The right move reveals itself to those who watch first.',
  'Communication flows easily now. Say what you mean plainly and doors open faster than usual.',
  'An old worry loosens its grip. Soon you will wonder why it ever felt so heavy.',
  'Focus beats hustle today. One task done fully outweighs ten started and abandoned.',
  'A surprising message shifts your plans for the better. Stay flexible and follow the opening.',
  'Your discipline shows visible results. Keep the routine that has quietly been working.',
  'Rest is productive today. Protect your energy and let the important things come to you.',
  'A brave conversation clears the air. Courage now saves weeks of misunderstanding later.',
  'Small luck follows you around today. Notice the green lights and keep moving.',
  'You are being asked to lead, even in a small way. Step up gently and others will follow.',
  'Let curiosity guide you. A detour today teaches something your plan never could.',
  'Balance is the lesson of the day. Give fairly to work, to people, and to yourself.',
  'Something you planted long ago finally sprouts. Your patience is quietly rewarded.',
  'Trust the process even when progress is invisible. Roots grow before the tree shows.',
  'A fresh perspective changes everything. Step back far enough to see the whole board.',
  'Your warmth draws the right people closer today. Stay open and let connection happen.',
  'Cut through the noise and name your one priority. Simplicity is your superpower right now.',
  'The day rewards honesty with yourself. Admit what you truly want, then act on it.',
]

const LOVE = [
  'An unexpected gesture rekindles warmth. Be the one who reaches out first.',
  'Honesty deepens a bond today — say the thing you have been holding back.',
  'Single? A connection sparks in an ordinary moment. Stay open and present.',
  'Give your partner space to be heard; listening is the love language of the day.',
  'Old tension dissolves with a small act of generosity. Lead with softness.',
  'Romance favours the patient. Let things breathe rather than forcing clarity.',
  'A meaningful conversation brings two paths closer together.',
  'Express appreciation out loud today. The simplest words land the deepest.',
  'Let go of keeping score. Love grows where generosity asks for nothing back.',
  'A shared laugh heals more than a long apology. Lighten the mood together.',
  'If single, confidence is your best attraction today — be unapologetically yourself.',
  'Plan something small and thoughtful. Effort speaks louder than grand gestures.',
  'Forgive a small slight and watch the closeness quietly return.',
  'Quality time matters more than gifts today. Put the phone away and be present.',
  'A vulnerable moment strengthens trust. Let your guard down just a little.',
  'Reconnect with someone you have drifted from. A short message could reopen a warm door.',
  'Patience with a loved one’s mood brings you closer, not further apart.',
  'Say yes to the invitation. New social ground could surprise your heart.',
  'Show love in your partner’s language, not only your own.',
  'A calm heart makes room for romance. Release the day’s stress before you connect.',
  'Speak your needs clearly and kindly. Clarity is a gift to those who love you.',
  'An honest compliment lifts someone’s whole day — and yours too.',
  'If tension lingers, choose gentleness over being right.',
  'A quiet evening together rebuilds what busy days wore down.',
  'Trust is built in small moments today. Show up when it counts.',
  'Give affection freely and without expectation. It returns multiplied.',
  'A fresh spark is possible even in a long bond. Try something new together.',
  'Let your heart be brave today. Say the words you keep rehearsing.',
  'Ask a real question and truly listen to the answer. Curiosity is intimacy.',
  'Warmth you offer today comes back when you least expect it.',
]

const CAREER = [
  'A quiet win positions you for recognition. Document it; it matters later.',
  'Collaboration outperforms solo effort today — loop in the right person.',
  'Resist over-committing. Protecting your focus is the strategic move now.',
  'A delayed decision finally turns in your favour. Be ready to act fast.',
  'Your judgment is sharp — trust it over the loudest voice in the room.',
  'Detail work pays off disproportionately today. Slow down to speed up.',
  'An opportunity hides inside a routine task. Look one layer deeper.',
  'Speak up in the meeting. Your idea is stronger than your doubt suggests.',
  'Finish what is nearly done before starting something new. Closure builds momentum.',
  'A mentor’s advice lands at the right time. Ask, and listen closely.',
  'Your reliability is being noticed quietly. Keep delivering and the reward follows.',
  'Say no to one distraction today and your best work gets done.',
  'Networking pays off now. A brief message to the right person opens a door.',
  'Organise before you execute. A clear plan turns effort into results.',
  'Take the lead on a small task. Initiative is remembered longer than talent.',
  'A challenge at work is a disguised opportunity. Meet it with a calm head.',
  'Follow up on the thing you left hanging. Persistence quietly wins today.',
  'Your creativity solves a stubborn problem. Trust the unconventional idea.',
  'Guard your morning; your sharpest thinking happens early.',
  'A small risk at work pays off. Back yourself and make the ask.',
  'Learn one new thing today. Skills compound faster than you expect.',
  'Clear communication prevents a costly mistake. Confirm the details.',
  'Celebrate a teammate’s success genuinely. Goodwill is career capital.',
  'Do the task you keep avoiding first. It frees the whole day.',
  'Money matters improve with one small, disciplined choice today.',
  'Your patience with a slow process pays off before long. Stay the course.',
  'A fresh idea deserves to be written down, not just thought. Capture it.',
  'Lead with questions, not answers, and the right solution appears.',
  'Steady effort beats a burst of hustle today. Keep a sustainable pace.',
  'Trust your preparation. When the moment comes, you are readier than you feel.',
]

const HEALTH = [
  'Hydration and a short walk reset your energy more than caffeine will.',
  'Your body is asking for earlier rest. Honour it and tomorrow rewards you.',
  'Breathwork or a few quiet minutes outdoors restores your balance.',
  'Channel restless energy into movement rather than worry.',
  'A light, mindful diet today leaves you noticeably clearer by evening.',
  'Stretch the tension out before it settles in. Small care, big effect.',
  'Mental rest counts as recovery — protect a pocket of stillness.',
  'Step away from screens for a while; your eyes and mind will thank you.',
  'A few deep breaths before reacting steady both body and mood.',
  'Sunlight early in the day lifts your energy for hours. Get outside.',
  'Nourish yourself with a proper meal instead of grabbing something quick.',
  'Gentle movement beats intense strain today. Listen to what your body wants.',
  'Sleep is your medicine tonight. Wind down earlier than usual.',
  'Drink more water than you think you need; low energy often hides thirst.',
  'A short walk after meals aids digestion and clears the head.',
  'Protect your peace today — stress shows up in the body first.',
  'Sit tall and breathe deep to release the tension you are holding.',
  'Trade one sugary habit for fruit today and feel the difference by evening.',
  'A screen-free hour before bed deepens your rest tonight.',
  'Move your body joyfully, not dutifully. Pick something you actually enjoy.',
  'Warmth and comfort matter today. Rest without guilt when you need it.',
  'A calm morning routine sets a steady tone for the whole day.',
  'Notice tension in your jaw and shoulders, then consciously let it go.',
  'Fresh air and a change of scene clear a foggy mind fast.',
  'Balance effort with recovery. Even a five-minute pause counts.',
  'Eat slowly and mindfully today; calm settles in along with the food.',
  'A little morning stretching keeps stiffness away all day.',
  'Your energy follows your breath. Slow it down and steadiness returns.',
  'Choose one healthy habit today rather than overhauling everything at once.',
  'Kindness toward your own body is the theme. Rest, hydrate, and breathe.',
]

const COLORS = [
  'Crimson', 'Emerald', 'Royal Blue', 'Amber', 'Saffron', 'Indigo', 'Turquoise',
  'Maroon', 'Gold', 'Silver', 'Coral', 'Sea Green', 'Ivory', 'Rose', 'Teal',
  'Lavender', 'Mustard', 'Copper', 'Sapphire', 'Peach', 'Olive', 'Burgundy',
  'Sky Blue', 'Pearl White',
]
const MANTRAS = [
  'Om Gam Ganapataye Namaha',
  'Om Namah Shivaya',
  'Om Suryaya Namaha',
  'Om Shri Mahalakshmyai Namaha',
  'Om Hraum Mitraya Namaha',
  'Om Chandraya Namaha',
  'Om Brihaspataye Namaha',
  'Om Shri Ganeshaya Namaha',
  'Om Aim Saraswatyai Namaha',
  'Om Dum Durgayai Namaha',
  'Om Namo Bhagavate Vasudevaya',
  'Om Shri Hanumate Namaha',
  'Om Shanti Shanti Shanti',
  'Om Kleem Krishnaya Namaha',
  'Om Shukraya Namaha',
  'Om Budhaya Namaha',
]
const MOODS = [
  'Optimistic', 'Grounded', 'Reflective', 'Energised', 'Calm', 'Determined',
  'Open-hearted', 'Focused', 'Playful', 'Confident', 'Serene', 'Curious',
  'Hopeful', 'Balanced', 'Inspired', 'Content', 'Bold', 'Patient', 'Grateful',
  'Motivated', 'Peaceful', 'Cheerful', 'Steady', 'Recharged',
]

/* Deterministic string hash → unsigned 32-bit int. */
function hash(str: string): number {
  let h = 2166136261
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return h >>> 0
}

function pick<T>(pool: T[], seed: number, salt: number): T {
  return pool[(seed + salt * 2654435761) % pool.length]
}

function todayKey(d: Date): string {
  return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`
}

function buildReading(sign: Sign, dateKey: string) {
  const seed = hash(`${sign.name}|${dateKey}`)
  return {
    overview: pick(OVERVIEW, seed, 1),
    love: pick(LOVE, seed, 2),
    career: pick(CAREER, seed, 3),
    health: pick(HEALTH, seed, 4),
    color: pick(COLORS, seed, 5),
    mantra: pick(MANTRAS, seed, 6),
    mood: pick(MOODS, seed, 7),
    lucky: (seed % 9) + 1,
    // 3–5 stars, derived from the seed so it's stable for the day.
    rating: 3 + (seed % 3),
  }
}

function Stars({ n }: { n: number }) {
  return (
    <span style={{ color: 'var(--accent-light)', fontSize: '14px', letterSpacing: '2px' }}>
      {'★'.repeat(n)}
      <span style={{ color: 'var(--text-muted)' }}>{'★'.repeat(5 - n)}</span>
    </span>
  )
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', gap: '12px', padding: '10px 0', borderTop: '1px solid var(--border)' }}>
      <span style={{ flexShrink: 0, width: '76px', fontSize: '12px', fontWeight: 700, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
        {label}
      </span>
      <span style={{ fontSize: '14px', color: 'var(--text-secondary)', lineHeight: 1.6 }}>{children}</span>
    </div>
  )
}

export default function HoroscopePage() {
  // Computed once on render. On a static export this is the user's
  // local "today" — readings rotate each calendar day.
  const { dateKey, prettyDate } = useMemo(() => {
    const now = new Date()
    return {
      dateKey: todayKey(now),
      prettyDate: now.toLocaleDateString('en-IN', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      }),
    }
  }, [])

  const readings = useMemo(
    () => SIGNS.map(sign => ({ sign, r: buildReading(sign, dateKey) })),
    [dateKey],
  )

  return (
    <div style={{ position: 'relative', zIndex: 1 }}>
      {/* ── Header ── */}
      <section style={{ background: 'var(--bg-secondary)', borderBottom: '1px solid var(--border)', padding: '60px 24px' }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
          <div style={{ marginBottom: '8px', fontSize: '12px', fontWeight: 700, letterSpacing: '0.15em', color: 'var(--accent)', textTransform: 'uppercase', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
            <Sun size={14} /> Daily Forecast
          </div>
          <h1 style={{ fontFamily: 'Playfair Display, serif', fontSize: 'clamp(36px, 5vw, 64px)', fontWeight: 900, color: 'var(--text-primary)', marginBottom: '14px' }}>
            Today&apos;s Horoscope
          </h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: '18px', maxWidth: '600px', lineHeight: 1.7, marginBottom: '20px' }}>
            Your free daily reading for all 12 zodiac signs — love, career, health, lucky number and a mantra to carry through the day.
          </p>
          <span style={{ display: 'inline-block', fontSize: '13px', fontWeight: 600, color: 'var(--accent)', background: 'rgba(232,131,42,0.1)', border: '1px solid var(--border)', padding: '6px 16px', borderRadius: '100px' }}>
            {prettyDate}
          </span>
        </div>
      </section>

      {/* ── Quick sign jump ── */}
      <div style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg-primary)', overflowX: 'auto' }}>
        <div style={{ maxWidth: '1280px', margin: '0 auto', padding: '14px 24px', display: 'flex', gap: '8px' }}>
          {SIGNS.map(s => (
            <a
              key={s.name}
              href={`#${s.name.toLowerCase()}`}
              style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                padding: '6px 14px', borderRadius: '100px', fontSize: '13px', fontWeight: 600,
                background: 'var(--bg-card)', color: 'var(--text-secondary)',
                border: '1px solid var(--border)', textDecoration: 'none', whiteSpace: 'nowrap',
              }}
            >
              <span style={{ fontSize: '15px' }}>{s.glyph}</span>
              {s.name}
            </a>
          ))}
        </div>
      </div>

      {/* ── Sign cards ── */}
      <div style={{ maxWidth: '1280px', margin: '0 auto', padding: '48px 24px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(340px, 100%), 1fr))', gap: '24px' }}>
          {readings.map(({ sign, r }) => (
            <div
              key={sign.name}
              id={sign.name.toLowerCase()}
              className="cosmic-card"
              style={{ padding: '28px', scrollMarginTop: '90px' }}
            >
              {/* Card header */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '18px' }}>
                <div className="icon-box" style={{ fontSize: '28px', width: '56px', height: '56px', flexShrink: 0 }}>
                  {sign.glyph}
                </div>
                <div style={{ flex: 1 }}>
                  <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: '24px', fontWeight: 800, color: 'var(--text-primary)', lineHeight: 1.1 }}>
                    {sign.name}
                  </h2>
                  <p style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '3px' }}>
                    {sign.dates} · {sign.element} · {sign.ruler}
                  </p>
                </div>
                <Stars n={r.rating} />
              </div>

              <p style={{ fontSize: '14px', color: 'var(--text-secondary)', lineHeight: 1.7, marginBottom: '14px' }}>
                {r.overview}
              </p>

              <Row label="Love">{r.love}</Row>
              <Row label="Career">{r.career}</Row>
              <Row label="Health">{r.health}</Row>

              {/* Lucky strip */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginTop: '16px' }}>
                {[
                  { k: 'Lucky No.', v: String(r.lucky) },
                  { k: 'Colour', v: r.color },
                  { k: 'Mood', v: r.mood },
                ].map(chip => (
                  <span
                    key={chip.k}
                    style={{
                      fontSize: '12px', fontWeight: 600, color: 'var(--text-secondary)',
                      background: 'var(--bg-secondary)', border: '1px solid var(--border)',
                      padding: '6px 12px', borderRadius: '100px',
                    }}
                  >
                    <span style={{ color: 'var(--text-muted)' }}>{chip.k}: </span>
                    {chip.v}
                  </span>
                ))}
              </div>

              <div style={{ marginTop: '14px', fontSize: '13px', color: 'var(--text-muted)', fontStyle: 'italic' }}>
                Mantra of the day — <span style={{ color: 'var(--accent)', fontStyle: 'normal', fontWeight: 600 }}>{r.mantra}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Disclaimer + app CTA */}
        <div
          style={{
            marginTop: '48px', padding: '28px', borderRadius: '16px',
            background: 'var(--bg-secondary)', border: '1px solid var(--border)',
            textAlign: 'center',
          }}
        >
          <p style={{ fontSize: '13px', color: 'var(--text-muted)', lineHeight: 1.7, maxWidth: '620px', margin: '0 auto 18px' }}>
            These daily readings are for guidance and entertainment. For a personalised forecast based on your exact birth chart,
            nakshatra and dashas, get a full reading inside the SarvamAstro app.
          </p>
          <Link href="/" className="btn-primary"><Sparkles size={18} /> Get Your Personal Birth Chart</Link>
        </div>
      </div>
    </div>
  )
}
